import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip } from "@mui/material";
import CsvImportDialog from "../../components/csv-import-dialog/CsvImportDialog";
import NewItemsPanel from "../../components/csv-import-dialog/NewItemsPanel";
import { describeMappings } from "../../components/csv-import-dialog/mappingHelpers";
import { baseAxios } from "../../utils/config";
import {
  setCategoryChoice,
  categoryMapToFields,
  categoryOptions,
} from "./importServicesHelpers";

const REQUIRED_COLUMNS = [
  "service_name",
  "shortcut_name",
  "description",
  "category",
];

const FORMAT_GUIDE = [
  "Status: Active or Inactive (blank means Active)",
  "Category: the category name. New names are created on import, or you can map them to an existing category in the preview",
  "Prices: price_level_1 to price_level_5, numbers with up to 2 decimals. At least one is required unless the service is free",
  "free_service and taxes_included: 1 (yes) or 0 (no)",
  "Service and shortcut names must be unique. Existing services are skipped",
];

const PREVIEW_COLUMNS = [
  { key: "service_name", label: "Name" },
  { key: "shortcut_name", label: "Shortcut" },
  {
    key: "category",
    label: "Category",
    render: (row) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        {row.category}
        {row.category_is_new && (
          <Chip label="new" size="small" color="warning" variant="outlined" />
        )}
      </Box>
    ),
  },
  { key: "status", label: "Status" },
];

const EXTRA_RESULT_COUNTS = [
  { key: "categories_created", label: "Categories created" },
];

/** Services CSV import. Flow and layout live in the shared CsvImportDialog. */
export default function ImportServicesDialog({
  open,
  onClose,
  onImportComplete,
}) {
  const [categoryMap, setCategoryMap] = useState({});
  const [categories, setCategories] = useState([]);

  // Fresh mapping and a current category list every time the dialog opens.
  useEffect(() => {
    if (!open) return undefined;
    setCategoryMap({});
    let cancelled = false;
    baseAxios
      .get("/categories")
      .then((res) => {
        if (!cancelled) setCategories(res.data?.categories ?? []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const extraFields = useMemo(
    () => categoryMapToFields(categoryMap),
    [categoryMap]
  );
  const options = useMemo(() => categoryOptions(categories), [categories]);

  const renderPreviewExtras = useCallback(
    ({ dryRunResult, revalidate, loading }) => {
      const choose = (name, categoryId) => {
        const next = setCategoryChoice(categoryMap, name, categoryId);
        setCategoryMap(next);
        // Pass the next fields explicitly: this component's state update has
        // not reached the dialog's props yet.
        revalidate(categoryMapToFields(next));
      };

      return (
        <NewItemsPanel
          title="New categories"
          noun="category"
          intro="These categories are not in your system yet. They will be created when you import, or you can use one of your existing categories instead."
          items={dryRunResult?.new_categories ?? []}
          mappings={describeMappings(categoryMap, options)}
          options={options}
          disabled={loading}
          onChoose={choose}
        />
      );
    },
    [categoryMap, options]
  );

  return (
    <CsvImportDialog
      open={open}
      onClose={onClose}
      onImportComplete={onImportComplete}
      entityPlural="services"
      entityTitle="Services"
      subtitle="Upload a CSV to add services in bulk."
      importPath="/services/import"
      templateFilename="service-import-template.csv"
      requiredColumns={REQUIRED_COLUMNS}
      optionalColumnsHint="Optional: status, price_level_1 to price_level_5, free_service, taxes_included, service_meevo_id. Column names are case-insensitive."
      formatGuide={FORMAT_GUIDE}
      previewColumns={PREVIEW_COLUMNS}
      existingIdKey="existing_service_id"
      extraResultCounts={EXTRA_RESULT_COUNTS}
      extraFields={extraFields}
      renderPreviewExtras={renderPreviewExtras}
    />
  );
}
